import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Legend, Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'
import AppDropdown from '../../../components/AppDropdown'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { parseStoredUser } from '../../../utils/authSession'
import { useUserAnalytics } from './hooks/useUserAnalytics'
import './UserAnalyticsScreen.css'

function formatHours(value) {
  return `${Number(value || 0).toFixed(1)}h`
}

function formatDurationFromSeconds(value) {
  const total = Math.max(Number(value || 0), 0)
  const totalMinutes = Math.round(total / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h ${String(minutes).padStart(2, '0')}m`
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
}

function getCategoryTone(category) {
  const normalized = String(category || '').toUpperCase()
  if (normalized.includes('ENGINEER')) return 'bg-primary-fixed text-on-primary-fixed'
  if (normalized.includes('DESIGN')) return 'bg-secondary-fixed text-on-secondary-fixed'
  return 'bg-tertiary-fixed text-on-tertiary-fixed'
}

function toIsoDate(input) {
  const parsed = new Date(input)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10)
}

function buildDateRangeList(dateFrom, dateTo) {
  const start = toIsoDate(dateFrom)
  const end = toIsoDate(dateTo)
  if (!start || !end) return []
  const values = []
  let pointer = new Date(start)
  const endDate = new Date(end)
  while (pointer <= endDate) {
    values.push(pointer.toISOString().slice(0, 10))
    pointer.setDate(pointer.getDate() + 1)
  }
  return values
}

function aggregateTrendByPreset(trendRows, granularity, dateFrom, dateTo) {
  const byDay = new Map()
    ; (trendRows || []).forEach((row) => {
      const dayKey = toIsoDate(row?.date)
      if (!dayKey) return
      const current = byDay.get(dayKey) || { productive: 0, idle: 0, tasks: 0 }
      current.productive += Number(row?.productiveSeconds || 0)
      current.idle += Number(row?.idleSeconds || 0)
      current.tasks += Number(row?.tasksCompleted || 0)
      byDay.set(dayKey, current)
    })

  const dateKeys = buildDateRangeList(dateFrom, dateTo)
  if (!dateKeys.length) {
    return []
  }

  const daily = dateKeys.map((iso) => {
    const row = byDay.get(iso) || { productive: 0, idle: 0, tasks: 0 }
    const tracked = row.productive + row.idle
    return {
      iso,
      productiveHours: Number((row.productive / 3600).toFixed(2)),
      idleHours: Number((row.idle / 3600).toFixed(2)),
      productivityPercent: tracked > 0 ? Number(((row.productive / tracked) * 100).toFixed(1)) : 0,
      efficiency: tracked > 0 ? Number(((row.productive / tracked) * 100).toFixed(1)) : 0,
      tasksCompleted: row.tasks,
    }
  })

  if (granularity === 'daily') {
    return daily.map((row, index) => ({
      label: `Day ${index + 1}`,
      rangeLabel: row.iso,
      productiveHours: row.productiveHours,
      idleHours: row.idleHours,
      productivityPercent: row.productivityPercent,
      efficiency: row.efficiency,
      tasksCompleted: row.tasksCompleted,
    }))
  }

  const bucketSize = granularity === 'monthly' ? 30 : 7
  const bucketCount = Math.max(1, Math.ceil(daily.length / bucketSize))
  const prefix = granularity === 'monthly' ? 'Month' : 'Week'
  const buckets = []

  for (let index = 0; index < bucketCount; index += 1) {
    const startIndex = index * bucketSize
    const slice = daily.slice(startIndex, startIndex + bucketSize)
    const productiveHours = Number(slice.reduce((sum, item) => sum + Number(item.productiveHours || 0), 0).toFixed(2))
    const idleHours = Number(slice.reduce((sum, item) => sum + Number(item.idleHours || 0), 0).toFixed(2))
    const tasksCompleted = slice.reduce((sum, item) => sum + Number(item.tasksCompleted || 0), 0)
    const trackedHours = productiveHours + idleHours
    const startDate = slice[0]?.iso || daily[startIndex]?.iso || ''
    const endDate = slice[slice.length - 1]?.iso || startDate
    buckets.push({
      label: `${prefix} ${index + 1}`,
      rangeLabel: startDate && endDate ? `${startDate} to ${endDate}` : '',
      productiveHours,
      idleHours,
      productivityPercent: trackedHours > 0 ? Number(((productiveHours / trackedHours) * 100).toFixed(1)) : 0,
      efficiency: trackedHours > 0 ? Number(((productiveHours / trackedHours) * 100).toFixed(1)) : 0,
      tasksCompleted,
    })
  }

  return buckets
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const data = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-recharts-tooltip">
      <p className="analytics-recharts-tooltip__title">{label}</p>
      <p className="analytics-recharts-tooltip__range">{data.rangeLabel || ''}</p>
      <div className="analytics-recharts-tooltip__row">
        <span className="analytics-recharts-tooltip__series">
          <span className="analytics-recharts-tooltip__dot analytics-recharts-tooltip__dot--productive" />
          Productive
        </span>
        <strong>{formatHours(data.productiveHours)}</strong>
      </div>
      <div className="analytics-recharts-tooltip__row">
        <span className="analytics-recharts-tooltip__series">
          <span className="analytics-recharts-tooltip__dot analytics-recharts-tooltip__dot--idle" />
          Idle
        </span>
        <strong>{formatHours(data.idleHours)}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

export default function UserAnalyticsScreen() {
  const navigate = useNavigate()
  const activeModules = useMemo(() => parseStoredUser().modules || [], [])
  const hasBruno = activeModules.includes('bruno')
  const hasNerve = activeModules.includes('nerve')

  const { state, actions } = useUserAnalytics()
  useBodyScrollLock(state.isDetailsOpen)
  const trendRows = state.insights?.coreEfficiency?.productivityTrend || []
  const allocationItems = state.insights?.currentAllocation?.items || []
  const ledgerRows = state.insights?.operationalLedger?.rows || []
  const teamSpan = state.insights?.teamSpan || {}
  const productiveVsIdle = state.insights?.productiveVsIdle || {}
  const userCost = state.insights?.userCostTrend || {}
  const efficiency = state.insights?.coreEfficiency || {}
  const peerIdleDelta = Number(productiveVsIdle.idleDeltaVsAvgHours || 0)
  const mappedAdditionalCosts = Array.isArray(userCost.mappedAdditionalCosts)
    ? userCost.mappedAdditionalCosts
      .map((item) => ({
        ...item,
        proratedAmount: Number(item?.proratedAmount || 0),
        monthlyEquivalent: Number(item?.monthlyEquivalent || 0),
        baseCost: Number(item?.baseCost || 0),
      }))
      .sort((a, b) => Number(b.proratedAmount || 0) - Number(a.proratedAmount || 0))
    : []

  const trendData = useMemo(
    () => aggregateTrendByPreset(trendRows, state.trendGranularity, state.dateFrom, state.dateTo),
    [trendRows, state.trendGranularity, state.dateFrom, state.dateTo],
  )
  const canViewCost = Boolean(state.insights?.permissions?.canViewCost)
  const drillColumns = Array.isArray(state.drilldown?.columns) ? state.drilldown.columns : []
  const drillRows = Array.isArray(state.drilldown?.rows) ? state.drilldown.rows : []

  const productiveHours = Number(productiveVsIdle.productiveHours || 0)
  const idleHours = Number(productiveVsIdle.idleHours || 0)
  const totalObservedHours = Math.max(productiveHours + idleHours, 0.1)
  const productiveMix = (productiveHours / totalObservedHours) * 100
  const idleMix = (idleHours / totalObservedHours) * 100
  const neutralMix = Math.max(0, 100 - productiveMix - idleMix)

  const dynamicAdditionalTotal = mappedAdditionalCosts.reduce((sum, item) => sum + Number(item.proratedAmount || 0), 0)
  const totalLoadedCost = Math.max(Number(userCost.totalLoadedCost || Number(userCost.directCompensation || 0) + dynamicAdditionalTotal), 1)
  const directMix = Math.max(0, (Number(userCost.directCompensation || 0) / totalLoadedCost) * 100)

  const spanStroke = Math.min(100, Number(teamSpan.directReports || 0) * 6.5)
  const spanDash = `${(276.3 * spanStroke) / 100} 276.3`
  const utilization = Math.max(0, Math.min(100, Number(state.insights?.currentAllocation?.overallUtilizationPercent || 0)))

  return (
    <div className="w-full max-w-[1600px] px-8 py-8">
      <section className="mb-8 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <h2 className="headline-font mb-2 text-5xl font-black leading-none tracking-tighter text-on-surface">User Performance</h2>
          <p className="max-w-md text-lg font-medium italic text-on-surface-variant">
            Real-time engagement and operational efficiency across the Bridge ecosystem.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-low p-2">
          <div className="flex min-w-[180px] flex-col border-r border-outline-variant/30 px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">User Context</span>
            <AppDropdown
              value={state.userId}
              onChange={(value) => actions.setUserId(String(value))}
              options={state.userOptions}
              placeholder="All Users"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[220px]"
            />
          </div>
          <div className="flex min-w-[160px] flex-col border-r border-outline-variant/30 px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Date Range</span>
            <AppDropdown
              value={state.presetDays}
              onChange={(value) => actions.applyPreset(String(value))}
              options={state.presetOptions}
              placeholder="Last 30 Days"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[170px]"
            />
          </div>
          <div className="flex min-w-[180px] flex-col px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Hierarchy</span>
            <AppDropdown
              value={state.departmentId}
              onChange={(value) => actions.setDepartmentId(String(value))}
              options={state.departmentOptions}
              placeholder="All Departments"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[210px]"
            />
          </div>
          <div className="flex min-w-[180px] flex-col border-l border-outline-variant/30 px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Reporting Manager</span>
            <AppDropdown
              value={state.reportingManagerId}
              onChange={(value) => actions.setReportingManagerId(String(value))}
              options={state.reportingManagerOptions}
              placeholder="All Managers"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[210px]"
            />
          </div>
          <div className="flex min-w-[180px] flex-col border-l border-outline-variant/30 px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Project</span>
            <AppDropdown
              value={state.projectId}
              onChange={(value) => actions.setProjectId(String(value))}
              options={state.projectOptions}
              placeholder="All Projects"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[220px]"
            />
          </div>
          <button
            type="button"
            className="material-symbols-outlined rounded-lg bg-white p-2 text-primary shadow-sm"
            aria-label="Reset filters"
            onClick={actions.resetFilters}
          >
            filter_list
          </button>
        </div>
      </section>

      {state.error ? <div className="analytics-user-status analytics-user-status-error mb-4">{state.error}</div> : null}
      {state.isLoading ? <div className="analytics-user-status mb-4">Loading analytics...</div> : null}

      <div className="grid grid-cols-12 gap-6">
        {hasBruno ? (
          <div className={`group relative col-span-12 overflow-hidden rounded-lg bg-surface-container-lowest p-8 ${hasNerve ? 'lg:col-span-8' : 'lg:col-span-12'}`}>
            <div className="mb-8 flex items-start justify-between">
              <div>
                <span className="headline-font rounded-full bg-primary-fixed px-3 py-1 text-xs font-bold text-primary-container">Core Efficiency</span>
                <h3 className="headline-font mt-4 text-3xl font-extrabold text-on-surface">Productivity Trend</h3>
              </div>
              <div className="cursor-pointer text-right" role="button" tabIndex={0} onClick={() => actions.openProductivity()} onKeyDown={(event) => { if (event.key === 'Enter') actions.openProductivity() }}>
                <div className="text-4xl font-black tracking-tighter text-primary">
                  {Number(efficiency.growthVsPreviousPeriodPercent || 0) >= 0 ? '+' : ''}
                  {formatPercent(efficiency.growthVsPreviousPeriodPercent)}
                </div>
                <div className="flex items-center justify-end gap-1 text-xs font-bold uppercase tracking-widest text-emerald-600">
                  <span>Growth vs Previous Period</span>
                  <AppTooltip content="Compared with the immediately previous equal-length date range.">
                    <button type="button" className="material-symbols-outlined text-sm text-emerald-600" aria-label="Growth period info">
                      info
                    </button>
                  </AppTooltip>
                </div>
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-on-surface-variant">
                  Current: {formatDurationFromSeconds(efficiency.productiveSeconds)} · Previous: {formatDurationFromSeconds(efficiency.previousProductiveSeconds)}
                </p>
                <p className="text-[11px] text-on-surface-variant">Change in productive hours versus the immediately preceding equal-length period.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="analytics-pill-toggle">
                  {[
                    ['hours', 'Productive Hours'],
                    ['percent', 'Productivity %'],
                    ['tasks', 'Tasks Completed'],
                    ['efficiency', 'Efficiency'],
                  ].map(([value, label]) => (
                    <button key={value} type="button" onClick={() => actions.setTrendMetric(value)} className={`analytics-pill-btn ${state.trendMetric === value ? 'analytics-pill-btn--active' : ''}`}>{label}</button>
                  ))}
                </div>
                <div className="analytics-pill-toggle">
                  {['daily', 'weekly', 'monthly'].map((value) => (
                    <button key={value} type="button" onClick={() => actions.setTrendGranularity(value)} className={`analytics-pill-btn capitalize ${state.trendGranularity === value ? 'analytics-pill-btn--active' : ''}`}>{value}</button>
                  ))}
                </div>
                <div className="analytics-pill-toggle">
                  {['chart', 'table'].map((value) => (
                    <button key={value} type="button" onClick={() => actions.setTrendView(value)} className={`analytics-pill-btn capitalize ${state.trendView === value ? 'analytics-pill-btn--active' : ''}`}>{value}</button>
                  ))}
                </div>
              </div>
            </div>
            {state.trendView === 'chart' ? <div className="h-72 min-w-0 w-full">
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={0}
                initialDimension={{ width: 800, height: 288 }}
              >
                <AreaChart
                  accessibilityLayer
                  data={trendData}
                  onClick={(chartData) => {
                    const point = chartData?.activePayload?.[0]?.payload
                    if (!point?.rangeLabel) return
                    const [periodFrom, periodTo = periodFrom] = point.rangeLabel.split(' to ')
                    actions.openProductivity(periodFrom, periodTo)
                  }}
                  style={{ cursor: 'pointer' }}
                  margin={{
                    left: 12,
                    right: 12,
                    top: 8,
                    bottom: 8,
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.18)" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={18} interval="preserveStartEnd" />
                  <YAxis width={54} unit={state.trendMetric === 'hours' ? 'h' : state.trendMetric === 'tasks' ? '' : '%'} label={{ value: state.trendMetric === 'hours' ? 'Hours' : state.trendMetric === 'tasks' ? 'Tasks' : 'Percent', angle: -90, position: 'insideLeft' }} />
                  <RechartsTooltip cursor={false} content={state.trendMetric === 'hours' ? <TrendTooltip /> : undefined} />
                  <Legend />
                  <defs>
                    <linearGradient id="fillProductive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.08} />
                    </linearGradient>
                    <linearGradient id="fillIdle" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.55} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.08} />
                    </linearGradient>
                  </defs>
                  {state.trendMetric === 'hours' ? <>
                    <Area dataKey="idleHours" type="natural" fill="url(#fillIdle)" fillOpacity={0.4} stroke="#f59e0b" stackId="a" name="Idle Hours" />
                    <Area dataKey="productiveHours" type="natural" fill="url(#fillProductive)" fillOpacity={0.45} stroke="var(--primary)" stackId="a" name="Productive Hours" />
                  </> : (
                    <Area
                      dataKey={state.trendMetric === 'tasks' ? 'tasksCompleted' : state.trendMetric === 'efficiency' ? 'efficiency' : 'productivityPercent'}
                      type="natural"
                      fill="url(#fillProductive)"
                      fillOpacity={0.45}
                      stroke="var(--primary)"
                      name={state.trendMetric === 'tasks' ? 'Tasks Completed' : state.trendMetric === 'efficiency' ? 'Efficiency %' : 'Productivity %'}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div> : (
              <div className="analytics-table-wrap max-h-72 overflow-auto">
                <table>
                  <thead><tr><th>Period</th><th>Date Range</th><th>Productive Hours</th><th>Idle Hours</th><th>Productivity %</th><th>Tasks Completed</th></tr></thead>
                  <tbody>
                    {trendData.map((row) => <tr key={`${row.label}-${row.rangeLabel}`} onClick={() => actions.openProductivity(row.rangeLabel?.split(' to ')[0], row.rangeLabel?.split(' to ')[1] || row.rangeLabel)} className="cursor-pointer"><td>{row.label}</td><td>{row.rangeLabel}</td><td>{formatHours(row.productiveHours)}</td><td>{formatHours(row.idleHours)}</td><td>{formatPercent(row.productivityPercent)}</td><td>{row.tasksCompleted}</td></tr>)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {hasNerve && canViewCost ? (
          <div className={`col-span-12 rounded-lg border border-outline-variant/10 bg-surface-container-low p-8 ${hasBruno ? 'lg:col-span-4' : 'lg:col-span-12'}`}>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary-container">payments</span>
              <h3 className="headline-font text-xl font-bold">User Cost Trend</h3>
              </div>
              <button type="button" className="text-xs font-bold text-primary" onClick={() => actions.openCostBreakdown()}>View sources</button>
            </div>
            <div className="space-y-6">
              <div>
                <div className="mb-2 flex justify-between text-xs font-bold text-on-surface-variant">
                  <span>Direct Compensation</span>
                  <span>{formatCurrency(userCost.directCompensation)}</span>
                </div>
                <AppTooltip content="CTC-based labor cost from timesheet activity">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, directMix)}%` }} />
                  </div>
                </AppTooltip>
              </div>
              {mappedAdditionalCosts.map((item, index) => {
                const amount = Number(item.proratedAmount || 0)
                const ratio = totalLoadedCost > 0 ? (amount / totalLoadedCost) * 100 : 0
                const colorClass = ['bg-primary-container', 'bg-secondary', 'bg-tertiary'][index % 3]
                return (
                  <div key={`user-cost-${item.id}-${index}`}>
                    <div className="mb-2 flex justify-between text-xs font-bold text-on-surface-variant">
                      <span>{item.reason || 'Additional Cost'}</span>
                      <span>{formatCurrency(amount)}</span>
                    </div>
                    <AppTooltip
                      content={`${item.costPeriod || 'MONTHLY'} | Base ${formatCurrency(item.baseCost)} | Monthly Eq ${formatCurrency(item.monthlyEquivalent)}`}
                    >
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                        <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${Math.min(100, ratio)}%` }} />
                      </div>
                    </AppTooltip>
                  </div>
                )
              })}
              {!mappedAdditionalCosts.length ? (
                <p className="text-xs font-medium text-on-surface-variant">No mapped additional costs for selected user context.</p>
              ) : null}
            </div>
            <div className="mt-12 border-t border-outline-variant/20 pt-6 text-center">
              <span className="text-3xl font-black tracking-tighter text-on-surface">{formatCurrency(userCost.totalLoadedCost)}</span>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Loaded Cost / Mo</p>
            </div>
          </div>
        ) : null}

        {hasBruno ? (
          <div className={`glass-panel col-span-12 rounded-lg border border-white/50 p-8 shadow-xl shadow-slate-200/50 md:col-span-6 ${hasNerve ? 'lg:col-span-4' : 'lg:col-span-6'}`}>
            <div className="mb-6 flex items-center gap-2">
              <h3 className="headline-font text-xl font-bold">Team Span</h3>
              <AppTooltip
                content={
                  <div className="space-y-1 text-[11px] leading-4">
                    <p><strong>Ratio:</strong> 1 : direct reports for the selected user.</p>
                    <p><strong>Direct Reports:</strong> Users where <code>reporting_manager_id</code> equals selected user.</p>
                    <p><strong>Org Depth:</strong> Deepest recursive reporting level under selected user.</p>
                  </div>
                }
              >
                <button
                  type="button"
                  className="material-symbols-outlined rounded-full bg-surface-container-low p-1 text-base text-primary"
                  aria-label="Team span calculation info"
                >
                  info
                </button>
              </AppTooltip>
            </div>
            <div className="relative flex items-center justify-center py-4">
              <div className="relative flex h-40 w-40 flex-col items-center justify-center rounded-full border-[12px] border-surface-container-high">
                <span className="text-4xl font-black text-primary">{teamSpan.spanRatio || '1:0'}</span>
                <span className="text-[10px] font-bold uppercase text-slate-500">Ratio</span>
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="#00327d" strokeDasharray={spanDash} strokeLinecap="round" strokeWidth="12" />
                </svg>
              </div>
            </div>
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-sm text-primary">groups</span>
                  <span className="text-sm font-bold text-on-surface">Direct Reports</span>
                </div>
                <span className="text-sm font-black text-on-surface-variant">{Number(teamSpan.directReports || 0)}</span>
              </div>
              <button type="button" className="w-full rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white" onClick={actions.openTeamHierarchy}>View Reporting Hierarchy</button>
              <div className="flex items-center justify-between rounded-xl bg-white/50 p-3">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-sm text-primary">hub</span>
                  <span className="text-sm font-bold text-on-surface">Org Depth</span>
                </div>
                <span className="text-sm font-black text-on-surface-variant">{Number(teamSpan.orgDepth || 0)} Levels</span>
              </div>
            </div>
          </div>
        ) : null}

        {hasBruno ? (
          <div className={`col-span-12 rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-8 md:col-span-6 ${hasNerve ? 'lg:col-span-4' : 'lg:col-span-6'}`}>
            <h3 className="headline-font mb-6 text-xl font-bold">Productive vs Idle</h3>
            <div className="mb-8 flex h-4 w-full overflow-hidden rounded-full">
              <div className="h-full bg-primary" style={{ width: `${Math.max(4, productiveMix)}%` }} />
              <div className="h-full bg-amber-400" style={{ width: `${Math.max(4, idleMix)}%` }} />
              <div className="h-full bg-slate-200" style={{ width: `${Math.max(0, neutralMix)}%` }} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => actions.openSessions()} className="rounded-2xl bg-surface-container-low p-4 text-left">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Tracked</span>
                <div className="text-2xl font-black text-on-surface">{formatDurationFromSeconds(productiveVsIdle.trackedSeconds)}</div>
                <p className="text-[10px] font-bold text-slate-500">100% observed time</p>
              </button>
              <button type="button" onClick={() => actions.openActivity()} className="rounded-2xl bg-surface-container-low p-4 text-left">
                <div className="mb-1 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Productive</span>
                </div>
                <div className="text-2xl font-black text-on-surface">{formatDurationFromSeconds(productiveVsIdle.productiveSeconds)}</div>
                <p className="text-[10px] font-bold text-emerald-600">{formatPercent(productiveVsIdle.productivePercent)}</p>
              </button>
              <button type="button" onClick={() => actions.openSessions()} className="rounded-2xl bg-surface-container-low p-4 text-left">
                <div className="mb-1 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-400" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Idle</span>
                </div>
                <div className="text-2xl font-black text-on-surface">{formatDurationFromSeconds(productiveVsIdle.idleSeconds)}</div>
                <p className="text-[10px] font-bold text-amber-600">
                  {formatPercent(productiveVsIdle.idlePercent)} · {peerIdleDelta >= 0 ? '+' : ''}{peerIdleDelta.toFixed(1)}h vs Avg
                </p>
              </button>
              <button type="button" onClick={() => actions.openActivity()} className="rounded-2xl bg-surface-container-low p-4 text-left">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Unclassified / Other</span>
                <div className="text-2xl font-black text-on-surface">{formatDurationFromSeconds(productiveVsIdle.unclassifiedSeconds)}</div>
                <p className="text-[10px] font-bold text-slate-500">{formatPercent(productiveVsIdle.unclassifiedPercent)}</p>
              </button>
            </div>
            <div className="mt-6 flex items-center gap-2 rounded-xl bg-primary-fixed/30 p-3">
              <span className="material-symbols-outlined text-base text-primary">info</span>
              <p className="text-[11px] font-medium leading-tight text-on-primary-fixed-variant">
                {productiveVsIdle.insight || 'Idle trend is currently stable in the selected window.'}
              </p>
            </div>
          </div>
        ) : null}

        {hasNerve ? (
          <div className={`relative col-span-12 flex flex-col justify-between overflow-hidden rounded-lg bg-primary p-8 text-white ${hasBruno ? 'lg:col-span-4' : 'lg:col-span-12'}`}>
            <div className="absolute -bottom-10 -right-10 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
            <div>
              <h3 className="headline-font mb-8 text-xl font-bold">Current Allocation</h3>
              <div className="relative z-10 space-y-6">
                {allocationItems.map((item, index) => {
                  const icons = ['architecture', 'data_thresholding', 'diversity_1']
                  const roleLabel = index === 0 ? 'Primary Project' : index === 1 ? 'Critical Path' : 'Internal'
                  return (
                    <AppTooltip
                      key={`${item.projectId || item.projectCode || index}-${index}`}
                      content={`${item.projectName || item.projectCode || 'Project'}: ${Number(item.capacityPercent || 0).toFixed(1)}% capacity`}
                    >
                      <button type="button" onClick={() => actions.setProjectId(String(item.projectId || ''))} className={`flex w-full items-start gap-4 text-left ${index === 2 ? 'opacity-60' : ''}`}>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                          <span className="material-symbols-outlined text-white">{icons[index] || 'work'}</span>
                        </div>
                        <div>
                          <div className="text-lg font-bold">{item.projectName || item.projectCode || 'Project'}</div>
                          <div className="text-xs font-medium text-on-primary-container">
                            {roleLabel} | {Number(item.capacityPercent || 0).toFixed(1)}% Capacity
                          </div>
                        </div>
                      </button>
                    </AppTooltip>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {hasNerve ? (
        <section className="mt-6 rounded-lg bg-surface-container-low p-8">
          <div className="mb-8 flex items-center justify-between">
            <h3 className="headline-font text-2xl font-extrabold text-on-surface">Operational Ledger</h3>
            <button type="button" onClick={() => actions.openLedger()} className="flex items-center gap-1 text-sm font-bold text-primary transition-all hover:gap-2">
              View Detailed Audit <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
          <div className="analytics-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Task/Identifier</th>
                  <th>Assigned Category</th>
                  <th>Hours Logged</th>
                  <th>Efficiency Rating</th>
                  {canViewCost ? <th className="text-right">Budget Impact</th> : null}
                </tr>
              </thead>
              <tbody>
                {ledgerRows.length ? (
                  ledgerRows.map((row, index) => (
                    <tr key={`${row.taskIdentifier || 'row'}-${index}`}>
                      <td className="font-bold text-on-surface">{row.taskIdentifier || '-'}</td>
                      <td>
                        <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${getCategoryTone(row.assignedCategory)}`}>
                          {row.assignedCategory || '-'}
                        </span>
                      </td>
                      <td>{Number(row.hoursLogged || 0).toFixed(2)}h</td>
                      <td>
                        <AppTooltip content="Efficiency now follows logged/planned * 100">
                          <span className="font-bold text-primary">{formatPercent(row.efficiencyRating)}</span>
                        </AppTooltip>
                      </td>
                      {canViewCost ? <td className="text-right">
                        <AppTooltip content="Labor cost only (approved timesheet hours x CTC/hr)">
                          <span className="font-bold">{formatCurrency(row.budgetImpact)}</span>
                        </AppTooltip>
                      </td> : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canViewCost ? 5 : 4} className="analytics-empty-cell">
                      No ledger rows available for selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {state.isDetailsOpen ? (
        <div className="analytics-user-drawer-overlay" onClick={actions.closeDetails} role="presentation">
          <aside className="analytics-user-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-user-drawer-header">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-on-surface-variant">User Analytics / {state.drillLevel}</p>
                <h3 className="headline-font text-3xl font-bold text-primary">{state.drillTitle}</h3>
                <p className="mt-2 text-sm text-on-surface-variant">User → Project → Task/Job → Activity → Time/Cost source records</p>
              </div>
              <button type="button" className="material-symbols-outlined" onClick={actions.closeDetails}>close</button>
            </div>
            <div className="analytics-user-drawer-content">
              <div className="analytics-user-drawer-nav">
                <button type="button" className="analytics-user-drawer-tab" onClick={actions.closeDetails}>Back</button>
                <button type="button" className="analytics-user-drawer-tab" onClick={() => actions.openProductivity(state.drillPeriodFrom, state.drillPeriodTo)}>Up One Level</button>
                {['productivity', 'allocation', 'team', 'ledger', ...(canViewCost ? ['cost'] : []), 'session', 'activity'].map((level) => (
                  <button key={level} type="button" className={`analytics-user-drawer-tab capitalize ${state.drillLevel === level ? 'active' : ''}`} onClick={() => actions.openDetails(`${level[0].toUpperCase()}${level.slice(1)} Details`, level, state.drillPeriodFrom, state.drillPeriodTo)}>{level}</button>
                ))}
                <button type="button" className="analytics-user-drawer-tab" onClick={actions.resetFilters}>Reset Drilldown</button>
                <button type="button" className="ml-auto rounded bg-primary px-3 py-2 text-xs font-bold text-white" onClick={() => actions.exportCurrentDrilldown('csv')} disabled={state.isExporting}>{state.isExporting ? 'Exporting…' : 'Export CSV'}</button>
                <button type="button" className="rounded border border-outline-variant/30 px-3 py-2 text-xs font-bold" onClick={() => actions.exportCurrentDrilldown('pdf')} disabled={state.isExporting}>Export PDF</button>
              </div>
              {state.drilldownError ? <p className="text-sm text-error">{state.drilldownError}</p> : null}
              {state.isLoadingDrilldown ? <p className="text-sm text-on-surface-variant">Loading drill-down records…</p> : null}
              <div className="analytics-user-list-card analytics-user-list-card--drawer">
                <div className="analytics-user-list-scroll">
                  <table className="analytics-user-list-table">
                    <thead><tr>{drillColumns.map((column) => <th key={column}>{column.replace(/([A-Z])/g, ' $1')}</th>)}<th>Source</th></tr></thead>
                    <tbody>
                      {drillRows.length ? drillRows.map((row, index) => <tr key={`${state.drillLevel}-${row.sourceType || ''}-${row.id || row.sessionId || index}`}>
                        {drillColumns.map((column) => <td key={column}>{typeof row[column] === 'number' ? row[column].toLocaleString('en-IN') : row[column] || '-'}</td>)}
                        <td>
                          {row.jobId || (state.drillLevel === 'ledger' && row.id) ? <button type="button" className="text-xs font-bold text-primary" onClick={() => navigate(`/nerve/job/entry?id=${row.jobId || row.id}`)}>View Job</button> : null}
                          {row.taskId ? <button type="button" className="ml-2 text-xs font-bold text-primary" onClick={() => navigate(`/nerve/task/entry?id=${row.taskId}`)}>View Task</button> : null}
                          {row.timesheetId ? <button type="button" className="ml-2 text-xs font-bold text-primary" onClick={() => navigate(`/nerve/timesheet/entry?id=${row.timesheetId}&mode=view`)}>View Timesheet</button> : null}
                          {row.projectId && !row.jobId && !row.taskId && !row.timesheetId ? <button type="button" className="text-xs font-bold text-primary" onClick={() => navigate(`/nerve/project/detail?id=${row.projectId}`)}>View Project</button> : null}
                          {!row.jobId && !row.taskId && !row.timesheetId && !row.projectId ? '-' : null}
                        </td>
                      </tr>) : <tr><td colSpan={drillColumns.length + 1} className="analytics-empty-cell">No drill-down records match the selected filters.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  )
}
